/**
 * Footer – podnožje sa linkom na vrh, politikom privatnosti i društvenim mrežama
 *
 * Dugme "Врх стране" skroluje na vrh; "Политика приватности" otvara modal sa DOCX sadržajem.
 */
import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import { DOCUMENT_TITLES } from '@/shared/constants';
import './Footer.css';

const PRIVACY_POLICY_URL = `${process.env.PUBLIC_URL || ''}/assets/${DOCUMENT_TITLES.PRIVACY_POLICY}`;

// ---------- Komponenta ----------
const Footer = () => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [documentHtml, setDocumentHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ---------- Rukovaoci ----------
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showPrivacyPolicyModal = () => {
    setShowPrivacyPolicy(true);
  };

  const handlePreviewClose = () => {
    setShowPrivacyPolicy(false);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  useEffect(() => {
    if (!showPrivacyPolicy) {
      return undefined;
    }

    let cancelled = false;

    const loadDocument = async () => {
      setIsLoading(true);
      setLoadError(null);
      setDocumentHtml('');

      try {
        const response = await fetch(PRIVACY_POLICY_URL);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (!cancelled) {
          setDocumentHtml(result.value);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Документ није могуће учитати. Покушајте поново касније.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [showPrivacyPolicy]);

  useEffect(() => {
    if (!showPrivacyPolicy) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowPrivacyPolicy(false);
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPrivacyPolicy]);

  // ---------- Render ----------
  return (
    <>
      <div className="">
        <div className="foot-top">
          <div className="container">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
              className="footer-scroll-top-button"
              aria-label="Повратак на врх стране"
            >
              Врх стране
            </button>
          </div>
        </div>
        <footer>
          <div className="container">
            <div className="foot-content">
              <div className="site-title">
                <h3>ПОУ Србија</h3>
                <p>Портале отворене управе Србије</p>
              </div>
              <div className="copy">
                Лиценца ©{' '}
                <a href="https://www.ite.gov.rs/" target="_blank" rel="noopener noreferrer" aria-label="ite.gov.rs (отвара се у новом прозору)">
                  ite.gov.rs
                </a>
              </div>
              <ul className="nav" aria-label="Линкови у подножју">
                <li className="emph">
                  <button
                    type="button"
                    onClick={showPrivacyPolicyModal}
                    className="footer-nav-button"
                    aria-label="Отвори политику приватности"
                  >
                    Политика приватности
                  </button>
                </li>
              </ul>
            </div>
            <ul className="foot-social" aria-label="Друштвене мреже">
              <li className="fb">
                <a
                  href="https://www.facebook.com/KancelarijaITE/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="foot-social-link"
                  aria-label="Посетите нашу Facebook страницу (отвара се у новом прозору)"
                >
                  Facebook
                </a>
              </li>
              <li className="tw">
                <a
                  href="https://twitter.com/kancelarijaITE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="foot-social-link"
                  aria-label="Посетите нашу Twitter страницу (отвара се у новом прозору)"
                >
                  Twitter
                </a>
              </li>
              <li className="py">
                <a
                  href="https://www.youtube.com/user/euprava"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="foot-social-link"
                  aria-label="Посетите наш YouTube канал (отвара се у новом прозору)"
                >
                  Play
                </a>
              </li>
            </ul>
          </div>
        </footer>
      </div>
      {showPrivacyPolicy && (
        <div className="modal-overlay" onClick={handlePreviewClose} role="dialog" aria-modal="true" aria-labelledby="privacy-policy-title">
          <div
            className={`modal-content${isFullscreen ? ' is-fullscreen' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-toolbar">
              <h2 id="privacy-policy-title">Политика приватности</h2>
              <div className="modal-toolbar-actions">
                <button
                  type="button"
                  className="modal-action-button"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? 'Изађи из целог екрана' : 'Цео екран'}
                >
                  {isFullscreen ? 'Умањи' : 'Цео екран'}
                </button>
                <button
                  type="button"
                  className="modal-action-button"
                  onClick={handlePreviewClose}
                  aria-label="Затвори политику приватности"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="privacy-policy-scroll">
              {isLoading && <p className="privacy-policy-status">Учитавање документа…</p>}
              {loadError && <p className="privacy-policy-status privacy-policy-error">{loadError}</p>}
              {!isLoading && !loadError && documentHtml && (
                <div className="privacy-policy-body" dangerouslySetInnerHTML={{ __html: documentHtml }} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
